import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import GlobalContext from 'contexts/GlobalContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Promotion, setPromoEntryModalOpen } from 'reducers/playerPromos';
import type { RootState } from 'store';

import PromoDetailsModal from './PromoDetailsModal';
import PromoEntryModal from './PromoEntryModal';

const PromoEntryAutoPrompt = () => {
  const dispatch = useAppDispatch();
  const { promotions = [] } = useAppSelector((state) => state.playerPromos);
  // One login modal per session (owner, 2026-09-24): the promo and the one-time
  // multi-run introduction are never both presented. While the introduction
  // holds this session's slot — or may yet, for the moment the player's flags
  // take to evaluate — the promo waits; if the introduction takes it, the promo
  // sits this session out and shows on the next login as usual. 'none' (also
  // the value when no introduction is mounted) leaves the promo unchanged.
  const featureIntroSlot = useAppSelector((state) => state.playerPromos.featureIntroSlot ?? 'none');
  // Read, not a subscription: the show effect below re-checks the slot when it
  // runs (see there).
  const store = useStore<RootState>();
  const isLobbyReady = useAppSelector((state) => state.lobby.isLobbyReady);
  const { activeView } = useContext(ViewManagerContext);
  // Blocks the auto-prompt whenever the InvalidLocationModal is on screen —
  // that modal is a full-screen play-blocker at z-index 1400+, and layering
  // the promo entry modal on top of it looked broken (promo card floated
  // over the "we cannot confirm your location" message, forcing the player
  // to dismiss two modals in a row before they could get to the Help /
  // Cashier / Profile actions). The gate is the same `isLocationInvalid`
  // signal InvalidLocationModal itself reads, so the two are always in sync:
  // when the location comes back valid, isLocationInvalid flips false, the
  // location modal closes, and the effect re-runs and prompts the promo
  // normally.
  const { isLocationInvalid } = useContext(GlobalContext);
  const shownPromoKeyRef = useRef<string | number | null>(null);
  const [entryPromo, setEntryPromo] = useState<Promotion | null>(null);
  const [detailsPromo, setDetailsPromo] = useState<Promotion | null>(null);

  const activePromo = useMemo(
    () => promotions.find((promo: Promotion) => promo.is_active),
    [promotions]
  );
  const activePromoKey = activePromo?.promo_id || activePromo?.promo_name || null;
  const canPrompt =
    featureIntroSlot === 'none' &&
    !isLocationInvalid &&
    activeView !== 'table' &&
    (activeView !== 'lobby' || isLobbyReady);

  useEffect(() => {
    if (!canPrompt || !activePromo || !activePromoKey) return;
    // The introduction claims the slot in a layout effect, which can land in
    // the same commit this effect's render belongs to — after `canPrompt` was
    // computed. Re-read it now, so the promo never opens on a stale 'none'.
    if ((store.getState().playerPromos.featureIntroSlot ?? 'none') !== 'none') return;
    if (shownPromoKeyRef.current === activePromoKey) return;
    if (isPromoEntryPromptSuppressed(activePromoKey)) return;

    shownPromoKeyRef.current = activePromoKey;
    setEntryPromo(activePromo);
  }, [activePromo, activePromoKey, canPrompt, store]);

  // If the location becomes invalid AFTER the promo modal has already been
  // shown (e.g. the geolocation timer flipped state a moment later), tear the
  // promo modal down immediately so the same layered-modal glitch doesn't
  // appear. The session-shown ref is deliberately NOT cleared here — once the
  // location comes back valid we don't want to spam the same promo again in
  // the same session. If the player wants it back, "View Details" opens the
  // details modal; the auto-prompt has already served its purpose.
  useEffect(() => {
    if (isLocationInvalid && entryPromo) setEntryPromo(null);
  }, [isLocationInvalid, entryPromo]);

  // Publish the entry-modal open state so the scrolling-notification ticker can
  // hold off rotating campaign messages until this modal is dismissed (Unity
  // parity). Reset to closed on unmount so the ticker isn't left paused.
  useEffect(() => {
    dispatch(setPromoEntryModalOpen(!!entryPromo));
    return () => {
      dispatch(setPromoEntryModalOpen(false));
    };
  }, [dispatch, entryPromo]);

  return (
    <>
      {entryPromo && (
        <PromoEntryModal
          promo={entryPromo}
          onClose={() => setEntryPromo(null)}
          onDontShowAgain={() => suppressPromoEntryPrompt(activePromoKey)}
          onViewDetails={() => {
            setDetailsPromo(entryPromo);
            setEntryPromo(null);
          }}
        />
      )}

      {detailsPromo && (
        <PromoDetailsModal promo={detailsPromo} onClose={() => setDetailsPromo(null)} />
      )}
    </>
  );
};

export default PromoEntryAutoPrompt;

const getPromoEntryPromptSuppressionKey = (promoKey: string | number) =>
  `promo-entry-prompt-dismissed:${promoKey}`;

const isPromoEntryPromptSuppressed = (promoKey: string | number) => {
  try {
    return window.localStorage.getItem(getPromoEntryPromptSuppressionKey(promoKey)) === '1';
  } catch {
    return false;
  }
};

const suppressPromoEntryPrompt = (promoKey: string | number | null) => {
  if (!promoKey) return;

  try {
    window.localStorage.setItem(getPromoEntryPromptSuppressionKey(promoKey), '1');
  } catch {
    // Ignore storage failures; dismissal still closes the current modal.
  }
};
